<template>
  <div v-if="allSections.length">
    <!-- options -->
    <div>
      <BaseButton
        :text="`Back to 'Cover details'`"
        flat
        ripple
        class="font-weight-bold d-flex ml-n5"
        icon="mdi-chevron-left"
        color="#106175"
        small
        @click="$router.push({ name: 'CoverTypePage' })"
      />

      <div class="d-flex justify-space-between mt-10">
        <h1 class="font-24 font-lg-28 font-weight-bold primary--text">
          Your details
        </h1>
        <BaseButton
          :text="$vuetify.breakpoint.lgAndUp ? 'Save &amp; exit' : 'Save'"
          flat
          depressed
          ripple
          class="font-weight-bold px-1 py-3"
          icon="mdi-content-save"
          color="#106175"
          small
          @click="toggleGlobalSaveAndExitDialog"
        />
      </div>
    </div>

    <!-- the form -->
    <ValidationObserver ref="yourDetailsForm">
      <form @submit.prevent="onSubmit">
        <ValidationObserver>
          <BaseLabel
            text="Are you insuring this property as an individual or as a business?"
            class="mt-10 mb-2"
          />
          <BaseToggleButton
            v-model="insuredPartyLocal"
            :toggleButtonText="{
              yes: 'Individual',
              yesDataUiid: 'InsuredParty_Individual',
              no: 'Business',
              noDataUiid: 'InsuredParty_Business',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            :min-width="$vuetify.breakpoint.lgAndUp ? '177' : ''"
          />

          <!-- company name form -->
          <template v-if="!insuredPartyLocal">
            <BaseLabel
              text="Business Name"
              helpText="If insuring as a limited company/business you should be a principle, director or partner of the business."
              class="mt-10 mb-2"
            />
            <BaseInput
              v-model="companyName.Value"
              dataUiid="CompanyName"
              hideDetails="auto"
              ariaLabel="Business Name"
              :rules="{
                required: {
                  required: true,
                  fieldLabel: 'Business Name',
                },
              }"
              :style="{
                'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
              }"
              :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            />
          </template>

          <BaseLabel text="Title" class="mt-10 mb-2" />
          <BaseSelect
            v-model="applicant1Title.Value"
            dataUiid="Applicant1Title"
            :items="setItemDataUiids('Applicant1Title', APPLICANT_TITLE_ITEMS)"
            :isEnableSortOrderListItems="false"
            :rules="{
              required: {
                required: true,
                fieldLabel: 'Title',
              },
            }"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '280px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            ariaLabel="Title"
          />

          <BaseLabel text="First name" class="mt-10 mb-2" />
          <BaseInput
            v-model="applicant1Forename.Value"
            dataUiid="Applicant1Forename"
            ariaLabel="First name"
            hideDetails="auto"
            :rules="{
              required: {
                required: true,
                fieldLabel: 'First name',
              },
              regex: {
                pattern: '^[a-zA-Z][a-zA-Z-\'\\s]*$',
                customMessage:
                  'First character must be a letter. Only letters, spaces, hyphens, and apostrophes are allowed in this field.',
              },
              min: {
                length: 2,
                fieldLabel: 'First name',
              },
              max: {
                length: 50,
                fieldLabel: 'First name',
              },
            }"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          />

          <BaseLabel text="Last name" class="mt-10 mb-2" />
          <BaseInput
            v-model="applicant1Surname.Value"
            dataUiid="Applicant1Surname"
            ariaLabel="Last name"
            hideDetails="auto"
            :rules="{
              required: {
                required: true,
                fieldLabel: 'Last name',
              },
              regex: {
                pattern: '^[a-zA-Z][a-zA-Z-\'\\s]*$',
                customMessage:
                  'First character must be a letter. Only letters, spaces, hyphens, and apostrophes are allowed in this field.',
              },
              min: {
                length: 2,
                fieldLabel: 'Last name',
              },
              max: {
                length: 100,
                fieldLabel: 'Last name',
              },
            }"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          />

          <BaseLabel text="Date of birth" class="mt-10 mb-2" />
          <BaseInputDate
            v-model="applicant1DoB.Value"
            dataUiid="Applicant1DoB"
            :rules="{
              required: {
                required: true,
                fieldLabel: 'Date of birth',
              },
              date_valid: {
                fieldLabel: 'Date of birth',
                errorMessageTemplate: 'Date of birth is not valid.',
              },
              regex: {
                pattern: '^\\s*$|^(\\d{2})\\/(\\d{2})\\/\\d{4}$',
                customMessage: 'Date of birth is not valid.',
              },
              age_in_range: {
                min: 18,
                max: 120,
              },
            }"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          />

          <BaseLabel
            text="Occupation"
            helpText="Please select your occupation from the options provided. If you cannot find an exact match just select the occupation closest to what you do."
            class="mt-3 mb-2"
          />
          <BaseAutocomplete
            v-model="applicant1Occupation.Value"
            placeholder="Start typing..."
            dataUiid="Applicant1Occupation"
            ariaLabel="Occupation"
            :items="allOccupations"
            :rules="{
              required: {
                required: true,
                fieldLabel: 'Occupation',
              },
            }"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          />

          <BaseLabel
            text="Industry"
            helpText="Please select the industry you work in from the options provided. If you cannot find an exact match just select the occupation closest to what you do."
            class="mt-10 mb-2"
          />
          <BaseAutocomplete
            v-model="applicant1Industry.Value"
            placeholder="Start typing..."
            ariaLabel="Industry"
            dataUiid="Applicant1Industry"
            :items="allIndustries"
            :rules="{
              required: {
                required: true,
                fieldLabel: 'Industry',
              },
            }"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          />

          <BaseToggle
            v-model="secondApplicantIncluded.Value"
            label="Add second applicant?"
            class="mt-10"
            @click="resetSecondApplicantFields"
            ariaLabel="Add second applicant?"
          />

          <!-- second applicant form -->
          <template v-if="secondApplicantIncluded.Value">
            <BaseLabel text="Title" class="mt-10 mb-2" />
            <BaseSelect
              v-model="applicant2Title.Value"
              dataUiid="Applicant2Title"
              :items="
                setItemDataUiids('Applicant2Title', APPLICANT_TITLE_ITEMS)
              "
              :isEnableSortOrderListItems="false"
              :rules="{
                required: {
                  required: true,
                  fieldLabel: 'Title',
                },
              }"
              :style="{
                'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
              }"
              :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              aria-label="Title"
            />

            <BaseLabel text="First name" class="mt-10 mb-2" />
            <BaseInput
              v-model="applicant2Forename.Value"
              dataUiid="Applicant2Forename"
              hideDetails="auto"
              :rules="{
                required: {
                  required: true,
                  fieldLabel: 'First name',
                },
                regex: {
                  pattern: '^[a-zA-Z][a-zA-Z-\'\\s]*$',
                  customMessage:
                    'First character must be a letter. Only letters, spaces, hyphens, and apostrophes are allowed in this field.',
                },
                min: {
                  length: 2,
                  fieldLabel: 'First name',
                },
                max: {
                  length: 50,
                  fieldLabel: 'First name',
                },
              }"
              :style="{
                'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
              }"
              :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              aria-label="First name"
            />

            <BaseLabel text="Last name" class="mt-10 mb-2" />
            <BaseInput
              v-model="applicant2Surname.Value"
              dataUiid="Applicant2Surname"
              hideDetails="auto"
              :rules="{
                required: {
                  required: true,
                  fieldLabel: 'Last name',
                },
                regex: {
                  pattern: '^[a-zA-Z][a-zA-Z-\'\\s]*$',
                  customMessage:
                    'First character must be a letter. Only letters, spaces, hyphens, and apostrophes are allowed in this field.',
                },
                min: {
                  length: 2,
                  fieldLabel: 'Last name',
                },
                max: {
                  length: 100,
                  fieldLabel: 'Last name',
                },
              }"
              :style="{
                'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
              }"
              :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              aria-label="Last name"
            />

            <BaseLabel text="Date of birth" class="mt-10 mb-2" />
            <BaseInputDate
              v-model="applicant2DoB.Value"
              dataUiid="Applicant2DoB"
              :rules="{
                required: {
                  required: true,
                  fieldLabel: 'Date of birth',
                },
                date_valid: {
                  fieldLabel: 'Date of birth',
                  errorMessageTemplate: 'Date of birth is not valid.',
                },
                regex: {
                  pattern: '^\\s*$|^(\\d{2})\\/(\\d{2})\\/\\d{4}$',
                  customMessage: 'Date of birth is not valid.',
                },
                age_in_range: {
                  min: 18,
                  max: 120,
                },
              }"
              :style="{
                'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
              }"
              :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            />

            <BaseLabel
              text="Occupation"
              helpText="Please select your occupation from the options provided. If you cannot find an exact match just select the occupation closest to what you do."
              class="mt-10 mb-2"
            />
            <BaseAutocomplete
              v-model="applicant2Occupation.Value"
              placeholder="Start typing..."
              dataUiid="Applicant2Occupation"
              :items="allOccupations"
              :rules="{
                required: {
                  required: true,
                  fieldLabel: 'Occupation',
                },
              }"
              :style="{
                'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
              }"
              :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              aria-label="Occupation"
            />

            <BaseLabel
              text="Industry"
              helpText="Please select the industry you work in from the options provided. If you cannot find an exact match just select the occupation closest to what you do."
              class="mt-10 mb-2"
            />
            <BaseAutocomplete
              v-model="applicant2Industry.Value"
              placeholder="Start typing..."
              dataUiid="Applicant2Industry"
              :items="allIndustries"
              :rules="{
                required: {
                  required: true,
                  fieldLabel: 'Industry',
                },
              }"
              :style="{
                'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
              }"
              :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              aria-label="Industry"
            />
          </template>

          <BaseLabel
            text="Have you previously held Landlord's insurance?"
            helpText="Please tell us if you have previously had landlord's insurance on ANY property in the United Kingdom.<br /><br />If you have never had landlord's insurance before, we automatically apply an introductory no claims discount of two years."
            class="mt-10 mb-2"
          />
          <BaseToggleButton
            v-model="previousLandlordsCover.Value"
            :toggleButtonText="{
              yesDataUiid: 'PreviousLandlordsCover_yes',
              noDataUiid: 'PreviousLandlordsCover_no',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
          />

          <BaseLabel
            text="How many properties do you own?"
            helpText="Please specify the amount of rental properties you currently own. If insuring in the name of a business, please confirm the number of properties owned by the business. <br><br>Unfortunately, we can't offer cover if you have more than 10 rental properties (including this one)."
            class="mt-10 mb-2"
          />
          <BaseRadio
            v-model="noOfRentalProperties.Value"
            pslStyle="button"
            :items="
              setItemDataUiids(
                'NoOfRentalProperties',
                NO_OF_RENTAL_PROPERTIES_ITEMS
              )
            "
            :rules="{
              required: {
                required: true,
                fieldLabel: 'How many properties do you own?',
              },
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            :width="$vuetify.breakpoint.lgAndUp ? '72' : '100'"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
            }"
            aria-label="How many properties do you own?"
          />

          <BaseLabel
            text="Is this insurance for a property that is subject to a new mortgage application?"
            helpText="If you are applying for a new mortgage or remortgaging to buy the property, please select 'Yes'.<br /><br />If you already have a mortgage approved for this property or own the property outright, please select 'No'."
            class="mt-10 mb-2"
          />
          <BaseToggleButton
            v-model="unapprovedMortgageApplication.Value"
            :toggleButtonText="{
              yesDataUiid: 'UnapprovedMortgageApplication_yes',
              noDataUiid: 'UnapprovedMortgageApplication_no',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
          />

          <BaseLabel
            text="Have you or a member of your family made any landlord’s claim in the last 5 years?"
            helpText="We define 'family' as you, your partner and children (including foster children) and any other person permanently living with you but excluding tenants.<br><br>You should include these incidents regardless of whether or not you made a claim and whether or not you were paid for the claim. If you paid out yourself, enter the value as £0.<br><br>If insuring as a limited company, you also need to declare any landlords claims made by the business within the last five years."
            class="mt-10 mb-2"
          />
          <BaseToggleButton
            v-model="previousClaim.Value"
            :toggleButtonText="{
              yesDataUiid: 'PreviousClaim_yes',
              noDataUiid: 'PreviousClaim_no',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
            @change="resetNoClaimsTable"
          />
        </ValidationObserver>

        <!-- previous claim form -->
        <template v-if="previousClaim.Value">
          <div class="mt-10">
            <!-- PreviousClaimsForm contains an ValidationObserver -->
            <BaseAlert
              v-if="isShowNoClaimsAlert"
              color="var(--red-100)"
              alertText="Please add your details of your claim below."
              alertTextClass="black--text"
              icon="mdi-alert-circle"
              class="field-invalid"
              iconColor="var(--red-500)"
            />

            <PreviousClaimsForm
              :previousClaimTypeOfLossOptions="
                LANDLORDS_PREVIOUS_CLAIM_TYPE_OF_LOSS_ITEMS
              "
              @add-table-items="addTableItems"
              @remove-table-items="removeTableItems"
            />
          </div>
        </template>

        <BaseButton
          depressed
          rounded
          dense
          class="font-weight-bold font-20 pa-7 px-13 mt-10 mb-3"
          type="submit"
          :color="'psorange'"
          :block="$vuetify.breakpoint.lgAndUp ? false : true"
          :text="
            $vuetify.breakpoint.lgAndUp
              ? 'CONTINUE TO PROPERTY DETAILS'
              : 'CONTINUE'
          "
        />
      </form>
    </ValidationObserver>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from "vuex";
import { ValidationObserver } from "vee-validate";
import { QUESTIONS_MODULE } from "@/store/types/module-types";
import {
  setItemDataUiids,
  APPLICANT_TITLE_ITEMS,
  NO_OF_RENTAL_PROPERTIES_ITEMS,
  LANDLORDS_PREVIOUS_CLAIM_TYPE_OF_LOSS_ITEMS,
} from "@/constants/component.constants.js";
import { ROUTE_NAME } from "@/constants/route.constants";
import { INSURED_PARTY } from "@/constants/common.constants";
import { scrollTop, tryGoToNextStep, convertToBool } from "@/common/utils";
import { populateClaimTable } from "@/common/claims";

import BaseLabel from "@/components/BaseComponents/BaseLabel";
import BaseToggleButton from "@/components/BaseComponents/BaseToggleButton";
import BaseInput from "@/components/BaseComponents/BaseInput";
import BaseInputDate from "@/components/BaseComponents/BaseInputDate";
import BaseButton from "@/components/BaseComponents/BaseButton";
import BaseSelect from "@/components/BaseComponents/BaseSelect";
import BaseAutocomplete from "@/components/BaseComponents/BaseAutocomplete";
import BaseToggle from "@/components/BaseComponents/BaseToggle";
import BaseRadio from "@/components/BaseComponents/BaseRadio";
import PreviousClaimsForm from "@/components/Forms/PreviousClaimsForm";
import BaseAlert from "@/components/BaseComponents/BaseAlert";

export default {
  name: "YourDetailsPage",
  components: {
    ValidationObserver,
    BaseLabel,
    BaseToggleButton,
    BaseInput,
    BaseInputDate,
    BaseButton,
    BaseSelect,
    BaseAutocomplete,
    BaseToggle,
    BaseRadio,
    PreviousClaimsForm,
    BaseAlert,
  },
  data() {
    return {
      setItemDataUiids,
      APPLICANT_TITLE_ITEMS,
      NO_OF_RENTAL_PROPERTIES_ITEMS,
      LANDLORDS_PREVIOUS_CLAIM_TYPE_OF_LOSS_ITEMS,
      QUESTIONS_MODULE,

      insuredParty: null,
      insuredPartyLocal: true,
      companyName: null,
      applicant1Title: null,
      applicant1Forename: null,
      applicant1Surname: null,
      applicant1DoB: null,
      applicant1Occupation: null,
      applicant1Industry: null,
      secondApplicantIncluded: null,
      previousLandlordsCover: null,
      unapprovedMortgageApplication: null,
      applicant2Title: null,
      applicant2Forename: null,
      applicant2Surname: null,
      applicant2DoB: null,
      applicant2Occupation: null,
      applicant2Industry: null,
      noOfRentalProperties: null,
      previousClaim: null,
      previousClaimValues: {},
      repeatingGroupPreviousClaim: {},
      isShowNoClaimsAlert: false,
    };
  },
  computed: {
    ...mapState(QUESTIONS_MODULE, [
      "quoteResponse",
      "restoringLastStep",
      "questionMap",
    ]),
    ...mapGetters(QUESTIONS_MODULE, [
      "allSections",
      "allIndustries",
      "allOccupations",
      "allQuestions",
    ]),
    isNoClaimValid() {
      const isNoClaimItems = !!this.repeatingGroupPreviousClaim.Value.length;

      // Valid if user added a claim, or said "No" and left fields empty
      const claimAdded = this.previousClaim.Value && isNoClaimItems;
      const claimNotAdded = !this.previousClaim.Value && !isNoClaimItems;
      return claimAdded || claimNotAdded;
    },
  },
  watch: {
    insuredPartyLocal(val) {
      if (val) {
        this.insuredParty.Value = "Individual";
      } else {
        this.insuredParty.Value = "Business";
      }
    },
    "previousClaim.Value"(value) {
      if (value !== undefined && !value) {
        this.isClaimFormValid = true;
        this.repeatingGroupPreviousClaim.Value = [];
        this.resetNoClaimsTable();
      }
    },
    "quoteResponse.Answers"() {
      this.populateClaimsValue();
    },
  },
  beforeMount() {
    if (this.allSections.length) {
      this.insuredParty = this.questionMap.InsuredParty;
      this.setInitialInsuredPartyValues();

      this.companyName = this.questionMap.CompanyName;
      this.applicant1Title = this.questionMap.Applicant1Title;
      this.applicant1Forename = this.questionMap.Applicant1Forename;
      this.applicant1Surname = this.questionMap.Applicant1Surname;
      this.applicant1DoB = this.questionMap.Applicant1DoB;
      this.applicant1Occupation = this.questionMap.Applicant1Occupation;
      this.applicant1Industry = this.questionMap.Applicant1Industry;
      this.secondApplicantIncluded = this.questionMap.SecondApplicantIncluded;
      this.previousLandlordsCover = this.questionMap.PreviousLandlordsCover;
      this.unapprovedMortgageApplication =
        this.questionMap.UnapprovedMortgageApplication;
      this.applicant2Title = this.questionMap.Applicant2Title;
      this.applicant2Forename = this.questionMap.Applicant2Forename;
      this.applicant2Surname = this.questionMap.Applicant2Surname;
      this.applicant2DoB = this.questionMap.Applicant2DoB;
      this.applicant2Occupation = this.questionMap.Applicant2Occupation;
      this.applicant2Industry = this.questionMap.Applicant2Industry;
      this.noOfRentalProperties = this.questionMap.NoOfRentalProperties;

      this.previousClaim = this.questionMap.PreviousClaim;
      this.repeatingGroupPreviousClaim =
        this.questionMap.RepeatingGroupPreviousClaim;
      this.previousClaimValues = this.questionMap.PreviousClaimValues;

      // when retrieving a quote convert "Boolean Strings" to Booleans
      this.secondApplicantIncluded.Value = convertToBool(
        this.secondApplicantIncluded.Value
      );
      this.previousLandlordsCover.Value = convertToBool(
        this.previousLandlordsCover.Value
      );
      this.unapprovedMortgageApplication.Value = convertToBool(
        this.unapprovedMortgageApplication.Value
      );
      this.previousClaim.Value = convertToBool(this.previousClaim.Value);
      if (this.previousClaim?.Value === true) {
        this.populateClaimsValue();
      }
    }
  },
  mounted() {
    scrollTop();

    if (!this.restoringLastStep) {
      return;
    }

    setTimeout(async () => {
      tryGoToNextStep(
        this.$refs.yourDetailsForm,
        async (form) => (await form.validate()) && this.isNoClaimValid,
        ROUTE_NAME.LANDLORDS.B2B.PROPERTY_DETAILS
      );
    }, 0);
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, [
      "saveProgress",
      "toggleGlobalSaveAndExitDialog",
      "resetFactFindSecondApplicantFields",
    ]),
    populateClaimsValue() {
      this.repeatingGroupPreviousClaim.Value = [];
      populateClaimTable(
        this.$store.state.questions,
        this.quoteResponse.Answers,
        this.repeatingGroupPreviousClaim
      );
    },
    async onSubmit() {
      const isFormValid = await this.$refs.yourDetailsForm.validate();

      if (!this.isNoClaimValid || !isFormValid) {
        const el = this.$el.querySelector(".field-invalid:first-of-type");
        this.$vuetify.goTo(el, { offset: 50 });
      }

      // when user selects YES and did NOT add a claim
      if (!this.isNoClaimValid) {
        this.isShowNoClaimsAlert = true;
      }

      if (isFormValid && this.isNoClaimValid) {
        this.saveProgress();
        this.isShowNoClaimsAlert = false;
        this.$router.push({ name: ROUTE_NAME.LANDLORDS.B2B.PROPERTY_DETAILS });
      }
    },
    addTableItems(tableItems) {
      this.isShowNoClaimsAlert = false;
      this.repeatingGroupPreviousClaim.Value = tableItems;
    },
    removeTableItems(tableItems) {
      this.isShowNoClaimsAlert = true;
      this.repeatingGroupPreviousClaim.Value = tableItems;
    },
    resetNoClaimsTable() {
      this.isShowNoClaimsAlert = false;
      this.repeatingGroupPreviousClaim.Value = [];
    },
    resetSecondApplicantFields() {
      this.applicant2Title.Value = "";
      this.applicant2Forename.Value = "";
      this.applicant2Surname.Value = "";
      this.applicant2DoB.Value = "";
      this.applicant2Occupation.Value = "";
      this.applicant2Industry.Value = "";

      // clear FF second applicant answers in state
      this.resetFactFindSecondApplicantFields();
    },
    setInitialInsuredPartyValues() {
      if (!this.insuredParty.Value) {
        this.insuredParty.Value = INSURED_PARTY.Individual; // default to Individual
      } else if (this.insuredParty.Value === INSURED_PARTY.Business) {
        this.insuredPartyLocal = false; // make the UI match the loaded answer
      }
    },
  },
};
</script>
